import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Mail, MessageSquare, Share2, Search, Users as UsersIcon, ShieldCheck, Briefcase, Eye, EyeOff, Lock, Check, X, History } from 'lucide-react';
import { getBackendUrl } from '../utils/getBackendUrl';
import MessageLogDialog from '../components/MessageLogDialog';

const API = `${getBackendUrl()}/api`;

const ROLE_LABEL = {
  admin: 'Admin',
  supervisor: 'Supervisor',
  employee: 'Employee',
  sub_contractor: 'Sub-Contractor',
  'sub-contractor': 'Sub-Contractor',
  subcontractor: 'Sub-Contractor',
  client: 'Client',
};

const ROLE_BADGE = {
  admin: 'bg-red-100 text-red-800 border-red-200',
  supervisor: 'bg-orange-100 text-orange-800 border-orange-200',
  employee: 'bg-blue-100 text-blue-800 border-blue-200',
  sub_contractor: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'sub-contractor': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  subcontractor: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

function initials(name) {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}

function digitsOnly(phone) {
  return (phone || '').replace(/\D/g, '');
}

export default function Connections({ user }) {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]); // for admin visibility selector
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [shareTarget, setShareTarget] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [visibilityTarget, setVisibilityTarget] = useState(null); // contact whose visibility is being edited
  const [visForm, setVisForm] = useState({ visible_to_all: true, visible_to_employees: [] });
  const [logTarget, setLogTarget] = useState(null); // contact whose message log is open
  const [unreadByUser, setUnreadByUser] = useState({}); // { otherUserId: count }
  // Compose modal: { contact, type: 'sms'|'email', subject, content }
  const [compose, setCompose] = useState(null);

  const isAdmin = user.role === 'admin';

  const fetchAll = async () => {
    setLoading(true);
    try {
      const calls = [
        axios.get(`${API}/connections`),
        axios.get(`${API}/projects`).catch(() => ({ data: [] })),
      ];
      if (isAdmin) calls.push(axios.get(`${API}/connections/employees`).catch(() => ({ data: [] })));
      const [u, p, e] = await Promise.all(calls);
      setUsers(Array.isArray(u.data) ? u.data : []);
      setProjects(Array.isArray(p.data) ? p.data : []);
      if (e) setEmployees(Array.isArray(e.data) ? e.data : []);
    } catch {
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); fetchUnread(); /* eslint-disable-next-line */ }, []);

  // Lightweight log helper — fire-and-forget so it never blocks the device-app handoff
  const logCommunication = (toUserId, type, extra = {}) => {
    axios.post(`${API}/communications`, {
      to_user_id: toUserId,
      type,
      content: extra.content || '',
      subject: extra.subject || '',
      direction: 'outgoing',
    }).then(() => fetchUnread()).catch(() => {});
  };

  const fetchUnread = async () => {
    try {
      const { data } = await axios.get(`${API}/communications/conversations`);
      const map = {};
      (data || []).forEach((c) => { if (c.unread_count > 0) map[c.other_user_id] = c.unread_count; });
      setUnreadByUser(map);
    } catch { /* ignore */ }
  };

  // Open the small composer that captures the message body BEFORE handing off to
  // the device's SMS / Mail app. iOS doesn't expose what the user typed in those
  // apps, so we record the body here and pre-fill the link so they don't retype.
  const openCompose = (contact, type) => {
    if (type === 'sms' && !digitsOnly(contact.phone)) {
      toast.error('No phone number on file');
      return;
    }
    if (type === 'email' && !contact.email) {
      toast.error('No email on file');
      return;
    }
    setCompose({ contact, type, subject: '', content: '' });
  };

  const sendCompose = () => {
    if (!compose) return;
    const { contact, type, subject, content } = compose;
    const body = (content || '').trim();
    if (!body) {
      toast.error('Type your message first');
      return;
    }

    logCommunication(contact.id, type, {
      subject: (subject || '').trim(),
      content: body,
    });

    if (type === 'sms') {
      const num = digitsOnly(contact.phone);
      window.location.href = `sms:${num}?&body=${encodeURIComponent(body)}`;
    } else if (type === 'email') {
      const subj = (subject || '').trim() || `Message from ${user.name || 'Dawal'}`;
      window.location.href = `mailto:${contact.email}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(body)}`;
    }
    setCompose(null);
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users
      .filter((u) => !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
      .sort((a, b) => {
        const aAdmin = a.role === 'admin' || a.role === 'supervisor';
        const bAdmin = b.role === 'admin' || b.role === 'supervisor';
        if (aAdmin !== bAdmin) return aAdmin ? -1 : 1;
        return (a.name || '').localeCompare(b.name || '');
      });
  }, [users, search]);

  const openVisibility = (contact) => {
    const v = contact.visibility || { visible_to_all: true, visible_to_employees: [] };
    setVisibilityTarget(contact);
    setVisForm({
      visible_to_all: v.visible_to_all !== false,
      visible_to_employees: Array.isArray(v.visible_to_employees) ? v.visible_to_employees : [],
    });
  };

  const toggleEmployeeAccess = (empId) => {
    setVisForm((f) => {
      const set = new Set(f.visible_to_employees);
      if (set.has(empId)) set.delete(empId); else set.add(empId);
      return { ...f, visible_to_employees: [...set] };
    });
  };

  const saveVisibility = async () => {
    if (!visibilityTarget) return;
    try {
      await axios.put(`${API}/connections/${visibilityTarget.id}/visibility`, visForm);
      toast.success('Visibility updated');
      setVisibilityTarget(null);
      await fetchAll();
    } catch {
      toast.error('Failed to update visibility');
    }
  };

  const handleShare = (contact) => {
    setShareTarget(contact);
    setSelectedProjectId('');
  };

  const performShare = (method) => {
    if (!selectedProjectId) {
      toast.error('Pick a project first');
      return;
    }
    const project = projects.find((p) => p.id === selectedProjectId);
    if (!project) return;

    const origin = window.location.origin;
    const link = `${origin}/projects/${project.id}`;
    const text = `Project: ${project.name}\n${project.address ? `Address: ${project.address}\n` : ''}${link}`;

    if (method === 'sms') {
      const num = digitsOnly(shareTarget.phone);
      if (!num) return toast.error('No phone number on file');
      logCommunication(shareTarget.id, 'sms', { content: text });
      window.location.href = `sms:${num}?&body=${encodeURIComponent(text)}`;
    } else if (method === 'email') {
      if (!shareTarget.email) return toast.error('No email on file');
      const subject = `Project Info: ${project.name}`;
      logCommunication(shareTarget.id, 'email', { subject, content: text });
      window.location.href = `mailto:${shareTarget.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;
    } else if (method === 'copy') {
      navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard'));
    }
    setShareTarget(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-28">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-center gap-2 mb-3">
          <UsersIcon className="w-6 h-6 text-blue-600" />
          <h1 className="text-xl font-bold text-gray-900">Connections</h1>
        </div>
        <p className="text-sm text-gray-500 mb-4">Reach the team — text, call, email, or share project info.</p>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            data-testid="connections-search"
          />
        </div>

        {loading ? (
          <div className="text-center text-sm text-gray-500 py-8">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-8">No team members found.</div>
        ) : (
          <div className="space-y-3" data-testid="connections-list">
            {filtered.map((c) => {
              const role = c.role || 'employee';
              const badge = ROLE_BADGE[role] || 'bg-gray-100 text-gray-800 border-gray-200';
              const roleIcon = (role === 'admin' || role === 'supervisor')
                ? <ShieldCheck className="w-3 h-3" />
                : <Briefcase className="w-3 h-3" />;
              const phoneDigits = digitsOnly(c.phone);
              return (
                <div
                  key={c.id}
                  className="bg-white rounded-xl border border-gray-100 shadow-sm p-4"
                  data-testid={`connection-${c.id}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white font-bold flex items-center justify-center text-base">
                      {initials(c.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900 truncate">{c.name || 'Unknown'}</p>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${badge}`}>
                        {roleIcon}
                        {ROLE_LABEL[role] || role}
                      </span>
                    </div>
                    {isAdmin && c.visibility && (
                      <button
                        type="button"
                        onClick={() => openVisibility(c)}
                        className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold border ${
                          c.visibility.visible_to_all
                            ? 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                            : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
                        }`}
                        title="Manage who can view this contact"
                        data-testid={`visibility-${c.id}`}
                      >
                        {c.visibility.visible_to_all ? <Eye className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                        {c.visibility.visible_to_all ? 'All' : `${c.visibility.visible_to_employees.length}`}
                      </button>
                    )}
                  </div>
                  <div className="space-y-1 text-xs text-gray-500 mb-3">
                    {c.email && <div className="truncate">📧 {c.email}</div>}
                    {c.phone && <div>📞 {c.phone}</div>}
                  </div>
                  <div className="grid grid-cols-5 gap-1.5">
                    <a
                      href={phoneDigits ? `tel:${phoneDigits}` : undefined}
                      onClick={(e) => {
                        if (!phoneDigits) { e.preventDefault(); toast.error('No phone number on file'); return; }
                        logCommunication(c.id, 'call');
                      }}
                      className={`flex flex-col items-center justify-center py-2 rounded-lg text-[11px] font-bold ${phoneDigits ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-gray-100 text-gray-400'}`}
                      data-testid={`call-${c.id}`}
                    >
                      <Phone className="w-4 h-4 mb-0.5" />
                      Call
                    </a>
                    <button
                      type="button"
                      onClick={() => phoneDigits ? openCompose(c, 'sms') : toast.error('No phone number on file')}
                      className={`flex flex-col items-center justify-center py-2 rounded-lg text-[11px] font-bold ${phoneDigits ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-gray-100 text-gray-400'}`}
                      data-testid={`text-${c.id}`}
                    >
                      <MessageSquare className="w-4 h-4 mb-0.5" />
                      Text
                    </button>
                    <button
                      type="button"
                      onClick={() => c.email ? openCompose(c, 'email') : toast.error('No email on file')}
                      className={`flex flex-col items-center justify-center py-2 rounded-lg text-[11px] font-bold ${c.email ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-gray-100 text-gray-400'}`}
                      data-testid={`email-${c.id}`}
                    >
                      <Mail className="w-4 h-4 mb-0.5" />
                      Email
                    </button>
                    <button
                      type="button"
                      onClick={() => handleShare(c)}
                      className="flex flex-col items-center justify-center py-2 rounded-lg text-[11px] font-bold bg-purple-500 hover:bg-purple-600 text-white"
                      data-testid={`share-${c.id}`}
                    >
                      <Share2 className="w-4 h-4 mb-0.5" />
                      Share
                    </button>
                    <button
                      type="button"
                      onClick={() => { setLogTarget(c); setUnreadByUser((m) => { const n = { ...m }; delete n[c.id]; return n; }); }}
                      className="relative flex flex-col items-center justify-center py-2 rounded-lg text-[11px] font-bold bg-gray-700 hover:bg-gray-800 text-white"
                      data-testid={`log-${c.id}`}
                    >
                      <History className="w-4 h-4 mb-0.5" />
                      Log
                      {unreadByUser[c.id] > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                          {unreadByUser[c.id] > 99 ? '99+' : unreadByUser[c.id]}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Share Project Modal */}
      {shareTarget && (
        <div
          className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center p-3"
          onClick={() => setShareTarget(null)}
          data-testid="share-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h3 className="text-lg font-bold text-gray-900">Share project with {shareTarget.name}</h3>
              <p className="text-xs text-gray-500 mt-0.5">Pick a project, then choose how to send.</p>
            </div>
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              data-testid="share-project-select"
            >
              <option value="">— Select a project —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => performShare('sms')}
                disabled={!selectedProjectId}
                className="py-2.5 rounded-lg text-xs font-bold bg-blue-500 hover:bg-blue-600 text-white disabled:bg-gray-200 disabled:text-gray-400"
                data-testid="share-sms-btn"
              >
                <MessageSquare className="w-4 h-4 mx-auto mb-0.5" />
                Text
              </button>
              <button
                onClick={() => performShare('email')}
                disabled={!selectedProjectId}
                className="py-2.5 rounded-lg text-xs font-bold bg-orange-500 hover:bg-orange-600 text-white disabled:bg-gray-200 disabled:text-gray-400"
                data-testid="share-email-btn"
              >
                <Mail className="w-4 h-4 mx-auto mb-0.5" />
                Email
              </button>
              <button
                onClick={() => performShare('copy')}
                disabled={!selectedProjectId}
                className="py-2.5 rounded-lg text-xs font-bold bg-gray-700 hover:bg-gray-800 text-white disabled:bg-gray-200 disabled:text-gray-400"
                data-testid="share-copy-btn"
              >
                <Share2 className="w-4 h-4 mx-auto mb-0.5" />
                Copy
              </button>
            </div>
            <button
              onClick={() => setShareTarget(null)}
              className="w-full text-sm text-gray-500 hover:text-gray-700 py-1"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {/* Visibility Modal (admin only) */}
      {visibilityTarget && (
        <div
          className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center p-3"
          onClick={() => setVisibilityTarget(null)}
          data-testid="visibility-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 space-y-4 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h3 className="text-lg font-bold text-gray-900">Who can see {visibilityTarget.name}?</h3>
              <p className="text-xs text-gray-500 mt-0.5">Admins always see everyone. Choose which employees see this contact.</p>
            </div>

            <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <div>
                <p className="font-semibold text-sm text-gray-900">Visible to all employees</p>
                <p className="text-xs text-gray-500">Turn OFF to restrict to specific employees.</p>
              </div>
              <button
                type="button"
                onClick={() => setVisForm((f) => ({ ...f, visible_to_all: !f.visible_to_all }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${visForm.visible_to_all ? 'bg-blue-600' : 'bg-gray-300'}`}
                data-testid="visibility-toggle-all"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${visForm.visible_to_all ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {!visForm.visible_to_all && (
              <div className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-gray-700">Pick allowed employees</p>
                  <span className="text-[10px] text-gray-500">{visForm.visible_to_employees.length} selected</span>
                </div>
                {employees.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No employees yet.</p>
                ) : (
                  <div className="space-y-1 max-h-60 overflow-y-auto pr-1" data-testid="visibility-employee-list">
                    {employees.map((emp) => {
                      const selected = visForm.visible_to_employees.includes(emp.id);
                      return (
                        <button
                          key={emp.id}
                          type="button"
                          onClick={() => toggleEmployeeAccess(emp.id)}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm border ${
                            selected ? 'bg-blue-100 border-blue-300 text-blue-900' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                          }`}
                          data-testid={`vis-emp-${emp.id}`}
                        >
                          <span className={`w-4 h-4 flex items-center justify-center rounded border ${selected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                            {selected && <Check className="w-3 h-3 text-white" />}
                          </span>
                          <span className="flex-1 text-left truncate">{emp.name || emp.email}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => setVisibilityTarget(null)}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold border border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveVisibility}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="save-visibility-btn"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Compose Message Modal — captures content before opening device SMS/Mail app */}
      {compose && (
        <div
          className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center p-3"
          onClick={() => setCompose(null)}
          data-testid="compose-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                {compose.type === 'sms' ? 'Text' : 'Email'} {compose.contact.name}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                We'll save a copy in your message log, then open your {compose.type === 'sms' ? 'Messages' : 'Mail'} app to send it.
              </p>
            </div>
            {compose.type === 'email' && (
              <input
                type="text"
                value={compose.subject}
                onChange={(e) => setCompose((c) => ({ ...c, subject: e.target.value }))}
                placeholder="Subject"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                data-testid="compose-subject"
              />
            )}
            <textarea
              value={compose.content}
              onChange={(e) => setCompose((c) => ({ ...c, content: e.target.value }))}
              placeholder={compose.type === 'sms' ? 'Type your text message…' : 'Type your email…'}
              rows={compose.type === 'sms' ? 4 : 6}
              autoFocus
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
              data-testid="compose-content"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setCompose(null)}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold border border-gray-200 text-gray-700 hover:bg-gray-50"
                data-testid="compose-cancel"
              >
                Cancel
              </button>
              <button
                onClick={sendCompose}
                disabled={!compose.content.trim()}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-200 disabled:text-gray-400"
                data-testid="compose-send"
              >
                Save & Open {compose.type === 'sms' ? 'Messages' : 'Mail'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Log Dialog */}
      {logTarget && (
        <MessageLogDialog
          contact={logTarget}
          currentUserId={user.id}
          onClose={() => { setLogTarget(null); fetchUnread(); }}
        />
      )}
    </div>
  );
}
